import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { TableBody, TableCell, TableRow, Button, IconButton, Icon } from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  'button-error': {
    color: theme.palette.error.main,
  },
  tableCell: {
    padding: '10px 16px',
    color: theme.palette.text.common,
    maxWidth: '300px',
  },
}));

const AppTableBody = props => {
  const {
    actions,
    data,
    alignment,
    columnsNumber,
    emptyRows,
    columns,
    systemField,
    extraActionsKeys,
    getRowStyle,
    actionsColumnWidth,
  } = props;
  const classes = useStyles();

  return (
    <TableBody>
      {data &&
        !!data.length &&
        data.map((row, rowIndex) => {
          // Get row data object for styling and disabled checks
          const rowData = Object.keys(row).reduce((acc, key) => {
            acc[key] = row[key].value;
            return acc;
          }, {});
          const rowStyle = getRowStyle ? getRowStyle(rowData) : {};

          return (
            <TableRow key={`row-${rowIndex}`} style={rowStyle}>
              {Object.keys(row).map((column, colIndex) => {
                const extColumnProps = {};
                if (columns[colIndex].columnWidth) {
                  extColumnProps.style = {
                    width: columns[colIndex].columnWidth,
                    maxWidth: columns[colIndex].columnWidth,
                  };
                }
                return !row[column].isVisible ? null : (
                  <TableCell
                    key={`col-${rowIndex}-${colIndex}`}
                    align={columns[colIndex].alignment || alignment}
                    classes={{
                      root: classes.tableCell,
                    }}
                    {...extColumnProps}
                  >
                    {React.isValidElement(row[column].value)
                      ? React.cloneElement(row[column].value, {
                          id: row[systemField] && row[systemField].value,
                        })
                      : row[column].value}
                  </TableCell>
                );
              })}
              {actions && !!actions.length && (
                <TableCell
                  align="center"
                  classes={{
                    root: classes.tableCell,
                  }}
                  style={
                    actionsColumnWidth
                      ? {
                          width: actionsColumnWidth,
                          maxWidth: actionsColumnWidth,
                        }
                      : undefined
                  }
                >
                  {actions.map((action, actionIndex) => {
                    const { onClick, icon, title, color, type, disabled } = action;
                    const isHidden =
                      action.getHidden && typeof action.getHidden === 'function' ? action.getHidden(rowData) : false;
                    if (isHidden) {
                      return null;
                    }
                    const resolvedIcon =
                      action.getIcon && typeof action.getIcon === 'function' ? action.getIcon(rowData) : icon;
                    const resolvedTitle =
                      action.getTitle && typeof action.getTitle === 'function' ? action.getTitle(rowData) : title;
                    const resolvedOnClick =
                      action.getOnClick && typeof action.getOnClick === 'function'
                        ? action.getOnClick(rowData)
                        : onClick;
                    const extraKeys = {};
                    if (Array.isArray(extraActionsKeys)) {
                      extraActionsKeys.forEach(key => {
                        if (
                          row[key] &&
                          row[key].value &&
                          row[key].value instanceof Object &&
                          Object.keys(row[key].value).length
                        ) {
                          extraKeys[`data-${key.toLowerCase()}`] = JSON.stringify(row[key].value);
                        } else {
                          extraKeys[`data-${key.toLowerCase()}`] = row[key] && row[key].value;
                        }
                      });
                    }
                    // Support disabled as function that receives row data
                    const isDisabled = typeof disabled === 'function' ? disabled(rowData) : disabled;
                    // Support getDisabled as function that receives row data
                    const isDisabledByGetter =
                      action.getDisabled && typeof action.getDisabled === 'function'
                        ? action.getDisabled(rowData)
                        : false;
                    const finalDisabled = isDisabled || isDisabledByGetter;
                    const buttonComponentOptions = {
                      key: `action-${rowIndex}-${actionIndex}`,
                      variant: 'text',
                      size: 'small',
                      color: 'primary',
                      className: color ? classes[`button-${color}`] : '',
                      'data-key': row[systemField] && row[systemField].value,
                      onClick: resolvedOnClick,
                      disabled: finalDisabled,
                      ...extraKeys,
                    };

                    let ButtonComponent = null;

                    switch (type) {
                      case 'icon':
                        ButtonComponent = (
                          <IconButton title={resolvedTitle} {...buttonComponentOptions}>
                            <Icon>{resolvedIcon || ''}</Icon>
                          </IconButton>
                        );
                        break;
                      default:
                        ButtonComponent = (
                          <Button title={resolvedTitle} {...buttonComponentOptions}>
                            {title || ''}
                          </Button>
                        );
                    }
                    return ButtonComponent;
                  })}
                </TableCell>
              )}
            </TableRow>
          );
        })}
      {emptyRows > 0 && (
        <TableRow style={{ height: 53 * emptyRows }}>
          <TableCell
            colSpan={columnsNumber}
            classes={{
              root: classes.tableCell,
            }}
          />
        </TableRow>
      )}
    </TableBody>
  );
};

AppTableBody.defaultProps = {
  emptyRows: 0,
  actionsColumnWidth: '',
};

AppTableBody.propTypes = {
  columnsNumber: PropTypes.number.isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      title: PropTypes.string,
      icon: PropTypes.string,
      color: PropTypes.string,
      onClick: PropTypes.func,
      getHidden: PropTypes.func,
      getIcon: PropTypes.func,
      getTitle: PropTypes.func,
      getOnClick: PropTypes.func,
    }),
  ).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      field: PropTypes.string.isRequired,
      alignment: PropTypes.string,
      isSorting: PropTypes.bool,
    }),
  ).isRequired,
  emptyRows: PropTypes.number,
  alignment: PropTypes.string.isRequired,
  actionsAlignment: PropTypes.string.isRequired,
  actionsColumnWidth: PropTypes.string,
  systemField: PropTypes.string.isRequired,
  extraActionsKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  getRowStyle: PropTypes.func,
};

export default AppTableBody;
